/**
 * Cadastro do cliente.
 *
 * Três campos e pronto. O telefone é o que importa: é por ele que a conta
 * encontra a ficha que o barbeiro já criou no balcão, e o histórico — inclusive
 * o tempo personalizado do cliente — vem junto sem ninguém precisar fazer nada.
 *
 * Aniversário e Instagram ficam atrás de um toque, fechados por padrão. Postos
 * no meio do formulário, viravam mais dois campos para encarar antes de
 * conseguir marcar um horário — e o cadastro precisa continuar cabendo num
 * minuto.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, ErrorNote, Field } from '../components/ui.tsx';
import { ApiError, api } from '../lib/api.ts';
import { useRefreshSession, useTenant } from '../lib/contexts.tsx';

export function SignUp() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const refresh = useRefreshSession();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [instagram, setInstagram] = useState('');
  const [extras, setExtras] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.post('/auth/sign-up', {
        name,
        phone,
        password,
        birthDate: birthDate || undefined,
        instagram: instagram.trim() || undefined,
      });
      await refresh();
      navigate('/agendar', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setLoading(false);
    }
  }

  return (
    <div className="pb-safe mx-auto flex min-h-full w-full max-w-sm flex-col justify-center gap-6 px-6 py-10">
      <div className="text-center">
        <h1 className="text-[22px] font-bold text-ink">Criar conta</h1>
        <p className="pt-1 text-[14px] text-ink-soft">na {tenant.name}</p>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field
          label="Seu nome"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoComplete="name"
          autoCapitalize="words"
          error={error?.fieldError('name')}
          required
        />

        <Field
          label="Telefone"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          autoComplete="tel"
          inputMode="tel"
          placeholder="(51) 99999-8888"
          error={error?.fieldError('phone')}
          hint="É por ele que a barbearia te reconhece"
          required
        />

        <Field
          label="Senha"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          error={error?.fieldError('password')}
          hint="No mínimo 8 caracteres"
          required
        />

        {extras ? (
          <>
            <Field
              label="Data de nascimento"
              type="date"
              value={birthDate}
              onChange={(event) => setBirthDate(event.target.value)}
              error={error?.fieldError('birthDate')}
              hint="Só para a barbearia lembrar do seu aniversário"
            />
            <Field
              label="Instagram"
              value={instagram}
              onChange={(event) => setInstagram(event.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="@seu.perfil"
              error={error?.fieldError('instagram')}
            />
          </>
        ) : (
          <button
            type="button"
            onClick={() => setExtras(true)}
            className="touch-target self-start text-[14px] text-accent"
          >
            + Aniversário e Instagram (opcional)
          </button>
        )}

        {error && !error.fieldError('phone') && !error.fieldError('password') && (
          <ErrorNote>{error.message}</ErrorNote>
        )}

        <Button type="submit" full loading={loading}>
          Criar conta
        </Button>
      </form>

      <p className="text-center text-[14px]">
        <Link to="/entrar" className="font-medium text-accent">
          Já tenho conta
        </Link>
      </p>
    </div>
  );
}
